import { STORAGE_KEY } from '@src/constants';
import { IDiscountPoint } from '@src/interfaces/discount.interfaces';

import { transliterate } from './string.helpers';

export const normalizePromoName = (name: string): string =>
  name
    .trim()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s-_]+/g, '_')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/ł/g, 'l')
    .replace(/ð/g, 'd')
    .replace(/þ/g, 'th')
    .replace(/[^a-z_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

export const generatePromoCode = async (
  name: string,
  date = new Date(),
): Promise<string> => {
  const normalizedName = normalizePromoName(await transliterate(name)).slice(
    0,
    10,
  );

  const month = new Intl.DateTimeFormat('en', {
    month: 'short',
  })
    .format(date)
    .toLowerCase();

  const year = String(date.getFullYear()).slice(-2);

  return `${normalizedName}_${month}${year}`;
};

export const getStoredPromoCode = (): string | null => {
  try {
    return sessionStorage.getItem(STORAGE_KEY.PROMO_CODE);
  } catch {
    return null;
  }
};

export const getDistance = (a: IDiscountPoint, b: IDiscountPoint) =>
  Math.hypot(b.x - a.x, b.y - a.y);

export const getPolylineLengths = (points: IDiscountPoint[]) => {
  const lengths: number[] = [];

  let total = 0;

  for (let i = 0; i < points.length - 1; i++) {
    const length = getDistance(points[i], points[i + 1]);

    lengths.push(length);
    total += length;
  }

  return {
    lengths,
    total,
  };
};

export const getPointOnPolyline = (
  points: IDiscountPoint[],
  progress: number,
): IDiscountPoint => {
  const { lengths, total } = getPolylineLengths(points);

  let distanceLeft = total * progress;

  for (let i = 0; i < lengths.length; i++) {
    const segmentLength = lengths[i];

    if (distanceLeft <= segmentLength) {
      const segmentProgress =
        segmentLength === 0 ? 0 : distanceLeft / segmentLength;

      const from = points[i];
      const to = points[i + 1];

      return {
        x: from.x + (to.x - from.x) * segmentProgress,
        y: from.y + (to.y - from.y) * segmentProgress,
      };
    }

    distanceLeft -= segmentLength;
  }

  return points[points.length - 1];
};

export const drawPolylineProgress = (
  ctx: CanvasRenderingContext2D,
  points: IDiscountPoint[],
  progress: number,
) => {
  const { lengths, total } = getPolylineLengths(points);

  let distanceLeft = total * progress;

  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);

  for (let i = 0; i < lengths.length; i++) {
    const segmentLength = lengths[i];

    const from = points[i];
    const to = points[i + 1];

    if (distanceLeft >= segmentLength) {
      ctx.lineTo(to.x, to.y);

      distanceLeft -= segmentLength;

      continue;
    }

    const segmentProgress =
      segmentLength === 0 ? 0 : distanceLeft / segmentLength;

    ctx.lineTo(
      from.x + (to.x - from.x) * segmentProgress,
      from.y + (to.y - from.y) * segmentProgress,
    );

    break;
  }

  ctx.stroke();
};

export const getIsApplyDiscount = <T>(offerCode: T): T | undefined =>
  JSON.parse(sessionStorage.getItem(STORAGE_KEY.APPLY_DISCOUNT) ?? 'false')
    ? offerCode
    : undefined;
